<!--
  TinoChatTable.vue — Active conversations table.
  Reads from ChatStore; falls back to mock data if empty.
-->
<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { useChatStore } from '@/stores/ChatStore'

const { t } = useI18n()
const chatStore = useChatStore()
const { conversations } = storeToRefs(chatStore)

const MOCK_ROWS = [
  { id: 1, name: 'Jordan Stevenson', channel: 'WhatsApp',  lastMessage: 'Let me speak to a human', time: '2 min ago',  status: 'human' },
  { id: 2, name: 'María García',     channel: 'Instagram', lastMessage: 'Let me speak to a human', time: '15 min ago', status: 'human' },
  { id: 3, name: 'Carlos López',     channel: 'WhatsApp',  lastMessage: 'Is this available?',      time: '32 min ago', status: 'bot' },
  { id: 4, name: 'Ana Martínez',     channel: 'Facebook',  lastMessage: 'Give me a discount',      time: '1 hr ago',   status: 'discount' },
]

const STATUS_CONFIG = {
  human:    { label: () => t('dashboard.tino.chat.statusHuman'),    bg: '#fff7ed', color: '#f59e0b' },
  bot:      { label: () => t('dashboard.tino.chat.statusBot'),      bg: '#eef2ff', color: '#6366f1' },
  discount: { label: () => t('dashboard.tino.chat.statusDiscount'), bg: '#fef2f2', color: '#ef4444' },
  resolved: { label: () => t('dashboard.tino.chat.statusResolved'), bg: '#f0fdf4', color: '#22c55e' },
  escalate: { label: () => t('dashboard.tino.chat.statusEscalate'), bg: '#fff7ed', color: '#f97316' },
}

const CHANNEL_COLORS = {
  WhatsApp:  '#25d366',
  Instagram: '#e1306c',
  Facebook:  '#1877f2',
}

function getInitials(name) {
  return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
}

function resolveStatus(conversation) {
  const aiStatus = conversation.ai_status
  if (aiStatus === 'HANDOFF_REQUESTED') return 'escalate'
  if (aiStatus === 'PAUSED_FOR_HUMAN') return 'human'
  if (aiStatus === 'TINO_ACTIVE') return 'bot'
  // fallback for mock rows
  if (conversation.status) return conversation.status
  const last = conversation.messages?.at(-1)
  if (last?.direction === 'incoming') return 'human'
  return 'bot'
}

const rows = computed(() => {
  if (conversations.value.length) {
    return conversations.value.slice(0, 8).map(c => ({
      id: c.id,
      name: c.participant?.fullName || 'Unknown',
      channel: c.participant?.role || 'WhatsApp',
      lastMessage: c.lastMessagePreview || '…',
      time: c.lastMessageAt ? new Date(c.lastMessageAt).toLocaleTimeString() : '—',
      status: resolveStatus(c),
    }))
  }
  return MOCK_ROWS
})

const totalLabel = computed(() => {
  const count = rows.value.length
  const total = conversations.value.length || 11284
  return `Rows per page: 10 · 1–${count} of ${total.toLocaleString()}`
})
</script>

<template>
  <VCard class="tino-chat-card" flat>
    <!-- Header -->
    <div class="tino-chat-header">
      <div class="d-flex align-center gap-2">
        <VIcon icon="mdi-message-outline" size="18" color="primary" />
        <span class="text-sm font-weight-semibold">{{ t('dashboard.tino.chat.title') }}</span>
      </div>
      <span class="text-xs text-medium-emphasis">{{ totalLabel }}</span>
    </div>

    <VDivider />

    <!-- Table -->
    <VTable density="comfortable" hover>
      <thead>
        <tr>
          <th class="tino-th">{{ t('dashboard.tino.chat.client') }}</th>
          <th class="tino-th">{{ t('dashboard.tino.chat.lastMessage') }}</th>
          <th class="tino-th">{{ t('dashboard.tino.chat.status') }}</th>
          <th class="tino-th">{{ t('dashboard.tino.chat.action') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in rows" :key="row.id">
          <!-- Client -->
          <td class="tino-td">
            <div class="d-flex align-center gap-3">
              <VAvatar size="32" color="primary" variant="flat" class="flex-shrink-0">
                <span class="text-xs font-weight-bold text-white">{{ getInitials(row.name) }}</span>
              </VAvatar>
              <div>
                <p class="text-xs font-weight-medium text-high-emphasis mb-0">{{ row.name }}</p>
                <p
                  class="text-[10px] d-flex align-center gap-1 mb-0"
                  :style="{ color: CHANNEL_COLORS[row.channel] || '#6366f1' }"
                >
                  <span
                    class="d-inline-block rounded-circle"
                    style="width: 6px; height: 6px;"
                    :style="{ background: CHANNEL_COLORS[row.channel] || '#6366f1' }"
                  />
                  {{ row.channel }}
                </p>
              </div>
            </div>
          </td>

          <!-- Last message -->
          <td class="tino-td">
            <p class="text-xs text-medium-emphasis mb-0 text-truncate" style="max-width: 200px">{{ row.lastMessage }}</p>
            <p class="text-[10px] text-disabled mb-0">{{ row.time }}</p>
          </td>

          <!-- Status -->
          <td class="tino-td">
            <VChip
              size="x-small"
              variant="flat"
              :style="{
                background: STATUS_CONFIG[row.status]?.bg || '#eef2ff',
                color: STATUS_CONFIG[row.status]?.color || '#6366f1',
              }"
            >
              {{ STATUS_CONFIG[row.status]?.label() || row.status }}
            </VChip>
          </td>

          <!-- Actions -->
          <td class="tino-td">
            <div class="d-flex align-center gap-1">
              <VBtn icon size="x-small" variant="text" color="primary" :title="t('dashboard.tino.chat.view')">
                <VIcon icon="mdi-eye-outline" size="16" />
              </VBtn>
              <VBtn icon size="x-small" variant="text" color="default" :title="t('dashboard.tino.chat.more')">
                <VIcon icon="mdi-dots-horizontal" size="16" />
              </VBtn>
            </div>
          </td>
        </tr>
      </tbody>
    </VTable>
  </VCard>
</template>

<style scoped lang="scss">
.tino-chat-card {
  border: 1px solid rgba(var(--v-border-color), 0.1);
  border-radius: 16px !important;
  background: rgb(var(--v-theme-surface));
  overflow: hidden;
}

.tino-chat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
}

.tino-th {
  font-size: 0.7rem !important;
  font-weight: 600 !important;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(var(--v-theme-on-surface), 0.45) !important;
  white-space: nowrap;
}

.tino-td {
  padding-block: 12px !important;
}
</style>
